import React, { useMemo } from 'react'

import Dayjs from 'dayjs'

import Box from 'components_new/atoms/Box'
import DatePicker from 'components_new/atoms/DatePicker'
import Icon from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'

import Condition, {
  isUnaryCondition
} from 'types/transformation/enums/filter/Condition'
import DataType from 'types/transformation/enums/DataType'
import FilterConditionType, {
  TODAY
} from 'types/transformation/enums/filter/Type'
import { FilterCondition } from 'types/transformation/types/nodes'

import FieldSelect, { FieldOption } from '../FieldSelect'
import FunctionSelect from '../FunctionSelect'
import { BOARD } from '../accents'

const BASE_CONDITIONS: { id: Condition; name: string }[] = [
  { id: Condition.EQ, name: 'är lika med' },
  { id: Condition.NE, name: 'inte är lika med' },
  { id: Condition.LT, name: 'är mindre än' },
  { id: Condition.LTE, name: 'är mindre än eller lika med' },
  { id: Condition.GT, name: 'är större än' },
  { id: Condition.GTE, name: 'är större än eller lika med' },
  { id: Condition.IS_NULL, name: 'är tom' },
  { id: Condition.IS_NOT_NULL, name: 'inte är tom' }
]

const STRING_ONLY_CONDITIONS: { id: Condition; name: string }[] = [
  { id: Condition.INCLUDES, name: 'inkluderar' },
  { id: Condition.EXCLUDES, name: 'inkluderar inte' },
  { id: Condition.STARTS_WITH, name: 'börjar med' },
  { id: Condition.ENDS_WITH, name: 'slutar med' }
]

type ValueKindOption = { id: FilterConditionType; name: string }

const STRING_VALUE_KINDS: ValueKindOption[] = [
  { id: FilterConditionType.NUMBER_OF_CHARACTERS, name: 'Antal tecken' },
  { id: FilterConditionType.INPUT_VALUE, name: 'Text' },
  { id: FilterConditionType.COLUMN, name: 'Kolumn' }
]

const NUMBER_VALUE_KINDS: ValueKindOption[] = [
  { id: FilterConditionType.INPUT_VALUE, name: 'Nummer' },
  { id: FilterConditionType.COLUMN, name: 'Kolumn' }
]

const DATE_VALUE_KINDS: ValueKindOption[] = [
  { id: FilterConditionType.STANDARD_VALUE, name: 'Idag' },
  { id: FilterConditionType.INPUT_VALUE, name: 'Specifikt datum' },
  { id: FilterConditionType.COLUMN, name: 'Kolumn' }
]

const DEFAULT_VALUE_KINDS: ValueKindOption[] = [
  { id: FilterConditionType.INPUT_VALUE, name: 'Text' },
  { id: FilterConditionType.COLUMN, name: 'Kolumn' }
]

function getConditionsForType(dataType?: DataType) {
  if (dataType === DataType.STRING) {
    return [...BASE_CONDITIONS, ...STRING_ONLY_CONDITIONS]
  }

  return BASE_CONDITIONS
}

function isDateLike(dataType?: DataType) {
  return dataType === DataType.DATE || dataType === DataType.TIMESTAMP
}

function isNumberLike(dataType?: DataType) {
  return dataType === DataType.BIGINT || dataType === DataType.DOUBLE
}

/**
 * Value-kind options depend on the left column's data type. Date/timestamp
 * columns offer the STANDARD_VALUE "Idag" option (today's date resolved at run
 * time); the other types omit it.
 */
function getValueKindsForType(dataType?: DataType): ValueKindOption[] {
  if (dataType === DataType.STRING) {
    return STRING_VALUE_KINDS
  }

  if (isNumberLike(dataType)) {
    return NUMBER_VALUE_KINDS
  }

  if (isDateLike(dataType)) {
    return DATE_VALUE_KINDS
  }

  return DEFAULT_VALUE_KINDS
}

interface FilterConditionProps {
  index: number
  condition: FilterCondition
  columns: FieldOption[]
  onChange: (index: number, next: FilterCondition) => void
  onRemove: (index: number) => void
}

/** A borderless text/number input that grows a green ring on focus. */
const ValueInput = (props: {
  value: string
  onChange: (value: string) => void
  numeric?: boolean
}) => (
  <Box
    sx={{
      backgroundColor: BOARD.rowBackground,
      border: '1px solid transparent',
      borderRadius: '9px',
      flex: 1,
      minWidth: 0,
      '&:focus-within': {
        backgroundColor: 'common.white',
        borderColor: BOARD.signalGreen
      }
    }}
  >
    <input
      className="nodrag ignore-locked-mode"
      value={props.value}
      onChange={(event) => props.onChange(event.target.value)}
      type={props.numeric ? 'number' : 'text'}
      style={{
        background: 'none',
        border: 'none',
        boxSizing: 'border-box',
        color: BOARD.textPrimary,
        fontFamily: BOARD.fontBody,
        fontSize: '13px',
        outline: 'none',
        padding: '7px 9px',
        width: '100%'
      }}
    />
  </Box>
)

export function FilterConditionRow(props: FilterConditionProps) {
  const { index, condition, columns, onChange, onRemove } = props

  const selectedColumn = useMemo(
    () => columns.find((column) => column.id === condition.left_column),
    [columns, condition.left_column]
  )

  const conditionOptions = useMemo(
    () => getConditionsForType(selectedColumn?.type),
    [selectedColumn?.type]
  )

  const rhsColumnOptions = useMemo(
    () => columns.filter((column) => column.type === selectedColumn?.type),
    [columns, selectedColumn?.type]
  )

  const valueKindOptions = useMemo(
    () => getValueKindsForType(selectedColumn?.type),
    [selectedColumn?.type]
  )

  const handleLeftAttribute = (id: string) => {
    onChange(index, { ...condition, left_column: id })
  }

  const handleCondition = (cond: Condition) => {
    const hasNoRhs = isUnaryCondition(cond)

    onChange(index, {
      ...condition,
      condition: cond,
      right_value: hasNoRhs ? null : condition.right_value,
      right_column: hasNoRhs ? null : condition.right_column
    })
  }

  const handleValueKind = (kind: FilterConditionType) => {
    onChange(index, {
      ...condition,
      type: kind,
      // STANDARD_VALUE carries the "today" sentinel; the backend resolves it to
      // the current date, so there is no user-entered right operand.
      right_value: kind === FilterConditionType.STANDARD_VALUE ? TODAY : null,
      right_column: null
    })
  }

  const handleRightValue = (value: string | null) => {
    onChange(index, { ...condition, right_value: value })
  }

  const handleRightColumn = (id: string) => {
    onChange(index, { ...condition, right_column: id })
  }

  const showValueRow =
    Boolean(selectedColumn) &&
    Boolean(condition.condition) &&
    !isUnaryCondition(condition.condition)

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.5 }}>
        <FieldSelect
          value={condition.left_column}
          options={columns}
          onChange={handleLeftAttribute}
          placeholder="Välj attribut"
          sx={{ flex: 1, minWidth: 0 }}
        />
        <IconButton onClick={() => onRemove(index)} size="small">
          <Icon name="Close" sx={{ fontSize: '15px' }} />
        </IconButton>
      </Box>

      {condition.left_column ? (
        <FunctionSelect
          value={condition.condition}
          options={conditionOptions}
          onChange={(value) => handleCondition(value as Condition)}
          menuWidth={230}
          sx={{ width: '100%' }}
        />
      ) : null}

      {showValueRow ? (
        <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.75 }}>
          <FunctionSelect
            value={condition.type}
            options={valueKindOptions}
            onChange={(value) => handleValueKind(value as FilterConditionType)}
            sx={{ flex: 'none', width: 132 }}
          />

          {/*
            STANDARD_VALUE ("Idag") carries an implicit value (today's date), so
            it renders no right-operand input.
          */}
          {condition.type !== FilterConditionType.STANDARD_VALUE &&
            (condition.type === FilterConditionType.COLUMN ? (
              <FieldSelect
                value={condition.right_column}
                options={rhsColumnOptions}
                onChange={handleRightColumn}
                placeholder="Välj kolumn"
                sx={{ flex: 1, minWidth: 0 }}
              />
            ) : condition.type === FilterConditionType.NUMBER_OF_CHARACTERS ? (
              <ValueInput
                value={condition.right_value ?? ''}
                onChange={handleRightValue}
                numeric
              />
            ) : isDateLike(selectedColumn?.type) ? (
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <DatePicker
                  value={condition.right_value}
                  onChange={(value) =>
                    handleRightValue(
                      value ? Dayjs(value).format('YYYY-MM-DD') : null
                    )
                  }
                  views={['year', 'month', 'day']}
                />
              </Box>
            ) : (
              <ValueInput
                value={condition.right_value ?? ''}
                onChange={handleRightValue}
                numeric={isNumberLike(selectedColumn?.type)}
              />
            ))}
        </Box>
      ) : null}
    </Box>
  )
}
